import {Page, ReportColumn, ReportConfig} from "./models/Reporting";
import {FveDiscountRateType} from "../ClientManagement/AssetReliance/AssetRelianceButtonState";
import {DataDisplayView} from "../ClientManagement/AssetAllocation/ReviseAssetAllocation";
import {FundingDisplayOptions} from "../Goals/Funding/models/Funding";
import {EstateFlowchartState} from "../EstateFlowchart/model/EstateFlowchartTypes";


const captureTheFactsPages = [
    "agenda",
    "familyTree",
    "assetsSummary",
    "currentNetWorth",
    "currentNetWorthAtDeath",
    "currentNetWorthDetailed",
    "currentNetWorthAtDeathDetailed",
    "assetGoalAlignment",
    "goalsSummary",
    "assetsSummaryDetailed",
    "goalsSummaryDetailed",
    "goalsPrioritization",
];

const portfolioReservePages = [
    "reviewPortfolioReserveDefinition",
    "reviewPortfolioReserve",
    "marketStress",
    "monitorPortfolioReserve"
];

const modelingPages = [
    "planSummary",
    "assetReliance",
    "currentPlan",
    "retireEarly",
    "retireToday",
    "compareScenarios",
    "assetRelianceDetailed",
    "assetRelianceDetailedWithFutureValue"
]

const investmentPlanPages = [
    "currentAssetAllocation",
    "proposedAssetAllocation",
    "currentVsProposed",
    "fulfillmentProposal",
    "currentVsProposedDetailed"
]

const holisticAdvicePages = [
    "wealthTransferInEstate",
    "wealthTransferInEstateFutureValue",
    "wealthTransferTotalWealth",
    "wealthPlanOptimizer"
]

const wealthTransferPages = [
    "assetTitlingAndDistribution",
    "wealthTransferCurrentValueDetailed",
    "wealthTransferFutureValueDetailed",
    "estateFlowChart",
    "assetTitlingAndDistributionDetailed",
    "wealthPlanOptimizer",
    "wealthTransferFutureValueSunsetDetailed"
]


export const getReportSections = (reportType: string,
                                  monitorPr?: boolean,
                                  isArchived?: boolean,
                                  showEstateFlowChart?: boolean,
                                  estateFlowCharts?: EstateFlowchartState[],
                                  enable2026SunsetExemption?: boolean): ReportColumn[] => {

    const wealthPlanningPages : Page [] =  [
        {
            label: 'Asset Titling',
            value: 'assetTitlingAndDistribution',
            isDisabled: false
        },
        {
            label: 'Asset Titling (Detailed)',
            value: 'assetTitlingAndDistributionDetailed',
            isDisabled: false
        }

    ]
    if(showEstateFlowChart && estateFlowCharts && estateFlowCharts.length>0)
    {
        wealthPlanningPages.push( {
            label: 'Estate Flowchart',
            value: 'estateFlowChart',
            isDisabled: false
        })
        estateFlowCharts.forEach(ele=> {
            let estateFlowChartPage : Page = {
                label: ele.flowchartName!,
                value: ele.flowchartName!,
                isDisabled: false,
                isFlowChartDocument: true
            }
            wealthPlanningPages.push(estateFlowChartPage)
        })
    }

    const wealthTransferFutureValueSunsetDetailed: Page[] = enable2026SunsetExemption ? [{
        label: 'Wealth Transfer - Future Value 2026 Sunset (Detailed)',
        value: 'wealthTransferFutureValueSunsetDetailed',
        isDisabled: false
    }] : [];

    const wealthPlanningPagesFinal : Page [] =  [
        ...wealthPlanningPages,
        {
            label: 'Wealth Plan Optimizer',
            value: 'wealthPlanOptimizer'
        },
        {
            label: 'Wealth Transfer - Present Value (Detailed)',
            value: 'wealthTransferCurrentValueDetailed',
        },
        {
            label: 'Wealth Transfer - Future Value (Detailed)',
            value: 'wealthTransferFutureValueDetailed',
        },
        ...wealthTransferFutureValueSunsetDetailed
    ]

    return (
        [

            {
                columnNumber: 1,
                sections: [
                    {
                        sectionTitle: 'Capture the Facts',
                        pagesList: captureTheFactsPages,
                        pages: [
                            {
                                label: 'Agenda',
                                value: 'agenda',
                            },
                            {
                                label: 'Family Tree',
                                value: 'familyTree',
                                isEditable: true,
                                isGreyedOut: reportType === 'Annual Report'
                            },
                            {
                                label: 'Asset Summary',
                                value: 'assetsSummary'
                            },
                            {
                                label: 'Asset Summary (Detailed)',
                                value: 'assetsSummaryDetailed',
                                isGreyedOut: reportType === 'Annual Report'
                            },
                            {
                                label: 'Current Net Worth',
                                value: 'currentNetWorth',
                            },
                            {
                                label: 'Current Net Worth (At Death)',
                                value: 'currentNetWorthAtDeath',
                            },
                            {
                                label: 'Current Net Worth (Detailed)',
                                value: 'currentNetWorthDetailed',
                            },
                            {
                                label: 'Current Net Worth (At Death Detailed)',
                                value: 'currentNetWorthAtDeathDetailed',
                            },
                            {
                                label: 'Asset-Goal Alignment',
                                value: 'assetGoalAlignment',
                                isDisabled: true
                            },
                            {
                                label: 'Goals Summary',
                                value: 'goalsSummary'
                            },
                            {
                                label: 'Goals Summary (Detailed)',
                                value: 'goalsSummaryDetailed',
                                isGreyedOut: reportType === 'Annual Report'
                            },
                            {
                                label: 'Goals Prioritization',
                                value: 'goalsPrioritization',
                            },
                            {
                                label: 'Goal Funding Over Time',
                                value: 'goalFunding',
                                isEditable: true,
                            }
                        ]
                    },
                ]
            },
            {
                columnNumber: 2,
                sections: [
                    {
                        sectionTitle: 'Portfolio Reserve',
                        pagesList: portfolioReservePages,
                        pages: [
                            {
                                label: 'Review Portfolio Reserve (Definition-Only)',
                                value: 'reviewPortfolioReserveDefinition'
                            },
                            {
                                label: 'Review Portfolio Reserve',
                                value: 'reviewPortfolioReserve',
                                isGreyedOut: reportType === 'Annual Report'
                            },
                            {
                                label: 'Market Stress',
                                value: 'marketStress',
                                isEditable: true,
                                isDisabled: true
                            },
                            {
                                label: `${!monitorPr ? 'Monitor Portfolio Reserve (not activated)' : 'Monitor Portfolio Reserve'}`,
                                value: 'monitorPortfolioReserve',
                                isGreyedOut: !monitorPr,
                            },
                        ]
                    },
                    {
                        sectionTitle: 'Investment Plan',
                        pagesList: investmentPlanPages,
                        pages: [
                            {
                                label: 'Current Asset Allocation',
                                value: 'currentAssetAllocation',
                                isDisabled: true
                            },
                            {
                                label: 'Proposed Asset Allocation',
                                value: 'proposedAssetAllocation',
                                isDisabled: true
                            },
                            {
                                label: 'Current vs. Proposed',
                                value: 'currentVsProposed',
                                isGreyedOut: reportType === 'Annual Report'
                            },
                            {
                                label: 'Current vs. Proposed (Detailed)',
                                value: 'currentVsProposedDetailed'
                            },
                            {
                                label: 'Fulfillment Proposal',
                                value: 'fulfillmentProposal',
                                isDisabled: true
                            },
                        ]
                    },
                    {
                        sectionTitle: 'Holistic Advice',
                        pagesList: holisticAdvicePages,
                        isDisabled: true,
                        pages: [
                            {
                                label: 'Wealth Transfer In-Estate',
                                value: 'wealthTransferInEstate',
                                isDisabled: true
                            },
                            {
                                label: 'Wealth Transfer In-Estate Future Value (Triple Net)',
                                value: 'wealthTransferInEstateFutureValue',
                                isDisabled: true
                            },
                            {
                                label: 'Wealth Transfer Total Wealth',
                                value: 'wealthTransferTotalWealth',
                                isDisabled: true
                            },
                            {
                                label: 'Wealth Plan Optimizer',
                                value: 'wealthPlanOptimizer',
                                isDisabled: true
                            }
                        ]
                    },
                ]
            },
            {
                columnNumber: 3,
                sections: [
                    {
                        sectionTitle: 'Modeling',
                        pagesList: modelingPages,
                        pages: [
                            {
                                label: 'Plan Summary',
                                value: 'planSummary'
                            },
                            {
                                label: !isArchived ? 'Compare Plans' : 'Compare Plans (unavailable in archived plans)',
                                value: 'comparePlans',
                                isEditable: !isArchived,
                                isGreyedOut: isArchived
                            },
                            {
                                label: 'Asset Reliance',
                                value: 'assetReliance',
                                isGreyedOut: reportType === 'Annual Report'
                            },
                            {
                                label: 'Current Plan',
                                value: 'currentPlan',
                                isDisabled: true
                            },
                            {
                                label: 'Retire Early',
                                value: 'retireEarly',
                                isDisabled: true
                            },
                            {
                                label: 'Retire Today',
                                value: 'retireToday',
                                isDisabled: true
                            },
                            {
                                label: 'Compare Scenarios',
                                value: 'compareScenarios',
                                isDisabled: true
                            },
                            {
                                label: 'Asset Reliance (Detailed)',
                                value: 'assetRelianceDetailed'
                            },
                            {
                                label: 'Asset Reliance - Future Value (Summary)',
                                value: 'assetRelianceWithFutureValue',
                                isEditable: true,
                                isDisabled: false
                            },
                            {
                                label: 'Asset Reliance - Future Value (Detailed)',
                                value: 'assetRelianceDetailedWithFutureValue',
                                isEditable: true
                            },
                            {
                                label: 'Net Worth Over Time',
                                value: 'netWorthOverTime',
                                isEditable: true,
                                isDisabled: false
                            }
                        ]
                    },
                    {
                        sectionTitle: 'Wealth Planning',
                        pagesList: wealthTransferPages,
                        isDisabled: false,
                        pages: [...wealthPlanningPagesFinal]
                    }]
            }
        ]);
}
export const initialSelectedPagesForAnnualReport: ReportConfig = {
    coverPage: {
        isEnabled: false
    },
    familyTree: {
        isEnabled: true
    },
    agenda: {
        isEnabled: false
    },
    goalsSummary: {
        isEnabled: false
    },
    goalsSummaryDetailed: {
        isEnabled: true
    },
    assetsSummary: {
        isEnabled: false
    },
    assetsSummaryDetailed: {
        isEnabled: true
    },
    currentNetWorth: {
        isEnabled: false
    },
    currentNetWorthAtDeath: {
        isEnabled: false
    },
    currentNetWorthDetailed: {
        isEnabled: false
    },
    currentNetWorthAtDeathDetailed: {
        isEnabled: false
    },
    planSummary: {
        isEnabled: false
    },
    reviewPortfolioReserveDefinition: {
        isEnabled: false
    },
    reviewPortfolioReserve: {
        isEnabled: true
    },
    comparePlans: {
        isEnabled: false
    },
    assetReliance: {
        isEnabled: true
    },
    assetRelianceDetailed: {
        isEnabled: false
    },
    currentVsProposed: {
        isEnabled: true
    },
    currentVsProposedDetailed: {
        isEnabled: false
    },
    wealthTransferCurrentValueDetailed: {
        isEnabled: false
    },
    wealthTransferFutureValueDetailed: {
        isEnabled: false
    },
    wealthTransferFutureValueSunsetDetailed: {
        isEnabled: false
    },
    goalsPrioritization: {
        isEnabled: false
    },
    disclaimerPage: {
        isEnabled: true
    },
    monitorPortfolioReserve: {
        isEnabled: false
    },
    wealthPlanOptimizer: {
        isEnabled: false
    },
    assetTitlingAndDistribution: {
        isEnabled: false
    },
    assetTitlingAndDistributionDetailed: {
        isEnabled: false
    },
    assetRelianceDetailedWithFutureValue: {
        isEnabled: false
    },
    assetRelianceWithFutureValue: {
        isEnabled: false
    },
    netWorthOverTime: {
        isEnabled: false,
        netWorthOverTimeOptions: {
            discountRate: FveDiscountRateType.TRIPLE_NET,
            insuranceIncluded: true
        }
    },
    goalFunding: {
        isEnabled: false,
        goalFundingOptions: {
            displayView: DataDisplayView.PERCENTAGE_VIEW,
            displayOptions: FundingDisplayOptions.ONLY_PORTFOLIO,
            fundingAge: 0
        }
    },
    estateFlowChart: {
        isEnabled: false,
        estateFlowChartDetails: []
    }
};